import { formatKenteken, stripKenteken } from "@theme/kenteken-api";
//#region assets/kenteken-input.ts
/**
* Dutch license plate (kenteken) input with RDW sidecode auto-formatting.
*/
var KentekenInput = class extends HTMLElement {
	/** @type {HTMLInputElement | null} */
	#display = null;
	/** @type {HTMLInputElement | null} */
	#hidden = null;
	connectedCallback() {
		this.#display = this.querySelector("[data-kenteken-display]");
		this.#hidden = this.querySelector("[data-kenteken-value]");
		if (!this.#display || !this.#hidden) return;
		this.#display.addEventListener("input", this.#handleInput);
		this.#display.addEventListener("paste", this.#handlePaste);
		this.#display.addEventListener("blur", this.#handleBlur);
	}
	disconnectedCallback() {
		this.#display?.removeEventListener("input", this.#handleInput);
		this.#display?.removeEventListener("paste", this.#handlePaste);
		this.#display?.removeEventListener("blur", this.#handleBlur);
	}
	#handleInput = () => {
		if (!this.#display || !this.#hidden) return;
		const cursorFromEnd = this.#display.value.length - (this.#display.selectionStart ?? 0);
		const formatted = formatKenteken(this.#display.value);
		this.#display.value = formatted;
		this.#hidden.value = stripKenteken(formatted);
		const nextCursor = Math.max(0, formatted.length - cursorFromEnd);
		this.#display.setSelectionRange(nextCursor, nextCursor);
		this.#emitIfComplete();
	};
	#handlePaste = (event) => {
		event.preventDefault();
		if (!this.#display || !this.#hidden) return;
		const pasted = event.clipboardData?.getData("text") ?? "";
		const formatted = formatKenteken(pasted);
		this.#display.value = formatted;
		this.#hidden.value = stripKenteken(formatted);
		this.#display.setSelectionRange(formatted.length, formatted.length);
		this.#emitIfComplete();
	};
	#handleBlur = () => {
		if (!this.#display || !this.#hidden) return;
		const formatted = formatKenteken(this.#display.value);
		this.#display.value = formatted;
		this.#hidden.value = stripKenteken(formatted);
		this.dispatchEvent(new CustomEvent("kenteken-input:blur", {
			bubbles: true,
			detail: { value: this.#hidden.value }
		}));
	};
	/**
	* Programmatic restore (e.g. session vehicle on page load).
	* @param {string} clean - Six-character plate without formatting.
	*/
	setPlateValue(clean = "") {
		if (!this.#display) this.#display = this.querySelector("[data-kenteken-display]");
		if (!this.#hidden) this.#hidden = this.querySelector("[data-kenteken-value]");
		if (!this.#display || !this.#hidden) return;
		const normalized = stripKenteken(clean);
		if (normalized.length === 6) {
			const formatted = formatKenteken(normalized);
			this.#display.value = formatted;
			this.#hidden.value = normalized;
			return;
		}
		this.#display.value = "";
		this.#hidden.value = "";
	}
	/** Fire when the plate has all 6 alphanumerics (typing or paste). */
	#emitIfComplete() {
		const value = this.#hidden?.value || "";
		if (value.length !== 6) return;
		this.dispatchEvent(new CustomEvent("kenteken-input:complete", {
			bubbles: true,
			detail: { value }
		}));
	}
};
if (!customElements.get("kenteken-input")) customElements.define("kenteken-input", KentekenInput);
//#endregion
export { formatKenteken, stripKenteken };
